import React from "react";
import { motion } from "framer-motion";
import { Award, Star } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { FOUNDER_IMG } from "@/i18n";

const ease = [0.22, 1, 0.36, 1];

export default function About() {
  const { t } = useLang();

  return (
    <section id="about" data-testid="about-section" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-5 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden glass-strong">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/20 via-transparent to-brand-orange/10" />
              <img
                src={FOUNDER_IMG}
                alt={t.about.founder}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220] via-transparent to-transparent" />

              {/* floating credibility card */}
              <div className="absolute bottom-5 left-5 right-5 glass-strong rounded-2xl p-4 flex items-center gap-3" data-testid="founder-card">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-orange grid place-items-center">
                  <Award className="h-4 w-4 text-white" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-white tracking-tight">{t.about.founder}</div>
                  <div className="text-[11px] text-white/55 font-mono-tight uppercase tracking-wider">{t.about.founder_role}</div>
                </div>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} className="h-3 w-3 fill-brand-orange text-brand-orange" />
                  ))}
                </div>
              </div>
            </div>

            {/* floating mini */}
            <div className="hidden lg:block absolute -right-6 top-12 glass-strong rounded-2xl px-4 py-3 ring-glow-blue animate-float">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 font-mono-tight">{t.about.since}</div>
              <div className="font-display text-2xl font-black text-white">2011</div>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.24em] text-brand-blue font-mono-tight mb-4">
              {t.about.eyebrow}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-[48px] lg:text-[56px] font-black tracking-tighter text-white leading-[1.02]">
              {t.about.title} <br />
              <span className="text-gradient-blue">{t.about.title2}</span>
            </h2>

            <p className="mt-7 text-white/70 leading-relaxed text-[15px] sm:text-base lg:text-lg max-w-xl">
              {t.about.p1}
            </p>
            <p className="mt-4 text-white/65 leading-relaxed text-[15px] sm:text-base max-w-xl">
              <strong className="text-white font-semibold">{t.about.p2.split('.')[0]}.</strong>
              {' '}{t.about.p2.split('.').slice(1).join('.')}
            </p>

            {/* Skills bars */}
            <div className="mt-10 space-y-4 max-w-lg" data-testid="skills-bars">
              {t.about.skills.map((s, i) => (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12.5px] text-white/75 font-medium">{s.name}</span>
                    <span className="text-[12.5px] font-mono-tight text-white/55">{s.value}%</span>
                  </div>
                  <div className="h-[3px] rounded-full bg-white/5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${s.value}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.1, ease, delay: 0.25 + i * 0.08 }}
                      className={`h-full rounded-full ${i % 2 === 0 ? "bg-gradient-to-r from-brand-blue to-brand-blue-soft" : "bg-gradient-to-r from-brand-orange to-brand-orange-soft"}`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
